import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { checkSupabaseConnection } from '../lib/debugUtils';

export default function Debug() {
  const [connectionStatus, setConnectionStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [envVars, setEnvVars] = useState<any>({});
  const [testResult, setTestResult] = useState<any>(null);
  
  useEffect(() => {
    async function checkConnection() {
      try {
        setLoading(true);
        const status = await checkSupabaseConnection();
        setConnectionStatus(status);
        
        // Check environment variables (safely)
        setEnvVars({
          supabaseUrlConfigured: !!import.meta.env.VITE_SUPABASE_URL,
          supabaseKeyConfigured: !!import.meta.env.VITE_SUPABASE_ANON_KEY,
        });
      } catch (error) {
        console.error("Error checking connection:", error);
        setConnectionStatus({
          authOk: false,
          dbOk: false,
          authenticated: false,
          userId: null,
          error: String(error)
        });
      } finally {
        setLoading(false);
      }
    }
    
    checkConnection();
  }, []);
  
  const runTestQuery = async () => {
    try {
      // Get user info if logged in
      const { data: authData } = await supabase.auth.getUser();
      
      // Try a simple query
      const { data, error } = await supabase.from('customers').select('count').limit(1);
      
      setTestResult({
        success: !error,
        authenticated: !!authData.user,
        userId: authData.user?.id,
        error: error ? `${error.message} - ${error.details || ''}` : null,
        data
      });
    } catch (e: any) {
      setTestResult({
        success: false,
        error: e.message || String(e),
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-semibold mb-4">Supabase Connection Debugger</h1>
        
        {loading ? (
          <div className="text-center py-4">Loading connection status...</div>
        ) : (
          <div className="space-y-6">
            <div className="border rounded-md p-4 bg-gray-50">
              <h2 className="text-lg font-medium mb-2">Connection Status</h2>
              <div className="grid grid-cols-2 gap-2">
                <div className="font-medium">Auth Working:</div>
                <div className={connectionStatus?.authOk ? "text-green-600" : "text-red-600"}>
                  {connectionStatus?.authOk ? "YES" : "NO"}
                </div>
                
                <div className="font-medium">DB Connection:</div>
                <div className={connectionStatus?.dbOk ? "text-green-600" : "text-red-600"}>
                  {connectionStatus?.dbOk ? "YES" : "NO"}
                </div>
                
                <div className="font-medium">Authenticated:</div>
                <div className={connectionStatus?.authenticated ? "text-green-600" : "text-red-600"}>
                  {connectionStatus?.authenticated ? "YES" : "NO"}
                </div>
                
                <div className="font-medium">User ID:</div>
                <div>{connectionStatus?.userId || "Not authenticated"}</div>
              </div>
            </div>
            
            <div className="border rounded-md p-4 bg-gray-50">
              <h2 className="text-lg font-medium mb-2">Environment Variables</h2>
              <div className="grid grid-cols-2 gap-2">
                <div className="font-medium">SUPABASE_URL:</div>
                <div className={envVars.supabaseUrlConfigured ? "text-green-600" : "text-red-600"}>
                  {envVars.supabaseUrlConfigured ? "Configured" : "Missing"}
                </div>
                
                <div className="font-medium">SUPABASE_ANON_KEY:</div>
                <div className={envVars.supabaseKeyConfigured ? "text-green-600" : "text-red-600"}>
                  {envVars.supabaseKeyConfigured ? "Configured" : "Missing"}
                </div>
              </div>
            </div>
            
            <div className="border rounded-md p-4 bg-gray-50">
              <h2 className="text-lg font-medium mb-2">Test Database Query</h2>
              <button 
                onClick={runTestQuery}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 mb-4"
              >
                Run Test Query
              </button>
              
              {testResult && (
                <div className="mt-2">
                  <div className="font-medium">Query Success:</div>
                  <div className={testResult.success ? "text-green-600" : "text-red-600"}>
                    {testResult.success ? "YES" : "NO"}
                  </div>
                  
                  {testResult.authenticated !== undefined && (
                    <>
                      <div className="font-medium mt-2">Authenticated:</div>
                      <div>{testResult.authenticated ? "YES" : "NO"}</div>
                    </>
                  )}
                  
                  {testResult.userId && (
                    <>
                      <div className="font-medium mt-2">User ID:</div>
                      <div>{testResult.userId}</div>
                    </>
                  )}
                  
                  {testResult.error && (
                    <>
                      <div className="font-medium mt-2 text-red-600">Error:</div>
                      <div className="text-red-600 break-all">{testResult.error}</div>
                    </>
                  )}
                </div>
              )}
            </div>
            
            <div className="text-sm text-gray-500">
              <p><strong>Note:</strong> If you see "Missing" for environment variables but the connection is working,
              it means your environment variables are set in Vercel but not exposed to the browser (which is good for security).
              If you're seeing connection errors, make sure your environment variables are correctly set in the Vercel dashboard.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
